/**
 * Bulk P&L compare — query params, selection rules, and chart series.
 * @see docs/features/pricer/payout-compare-frontend.md
 */

import { fetchPayoutCompare } from "api/pricerApi";
import { PAYOUT_PRICING_SIDES } from "constants/payout";
import { formatPayoutAnalysisApiErrors } from "helpers/payoutAnalysisApi";

export const PAYOUT_COMPARE_MIN_OPTIONS = 2;
export const PAYOUT_COMPARE_MAX_OPTIONS = 10;
export const PAYOUT_COMPARE_ACTION_KEY = "payout_compare";

export const PAYOUT_COMPARE_Y_MODES = {
  PNL: "pnl",
  PERCENT: "percent",
};

const COMPARE_LINE_COLORS = [
  "#52c41a",
  "#3995ff",
  "#ffa500",
  "#ff4d4f",
  "#722ed1",
  "#13c2c2",
  "#eb2f96",
  "#2f54eb",
  "#fa8c16",
  "#a0d911",
];

export function uniqueSummaryIdsFromRows(rows) {
  const ids = [];
  const seen = new Set();
  (rows ?? []).forEach((row) => {
    const id = row?.summary_id ?? row?.summary_reference_id;
    if (id == null || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
}

export function isPayoutCompareSelectionCount(count) {
  return (
    count >= PAYOUT_COMPARE_MIN_OPTIONS && count <= PAYOUT_COMPARE_MAX_OPTIONS
  );
}

/**
 * Auth payload `authorized_action` includes `payout_compare`. If that list is
 * missing (older login payloads), show the item — same as ungated Payout.
 */
export function canUsePayoutCompare(loginInfo) {
  const actions =
    loginInfo?.authorized_action ?? loginInfo?.authorized_actions;
  if (!Array.isArray(actions)) return true;
  return actions.includes(PAYOUT_COMPARE_ACTION_KEY);
}

export function buildPayoutCompareQueryParams(options = {}) {
  const {
    ids,
    side,
    chart_increment,
    starting_ul_settlement_price,
    points_count,
    days_elapsed,
    volatility_override,
    risk_free_override,
    popup_session,
  } = options;

  const params = {};

  if (Array.isArray(ids) && ids.length > 0) {
    params.ids = ids;
  }

  if (side === PAYOUT_PRICING_SIDES.BID || side === PAYOUT_PRICING_SIDES.ASK) {
    params.side = side;
  }

  if (
    chart_increment != null &&
    chart_increment !== "" &&
    Number(chart_increment) > 0
  ) {
    params.chart_increment = chart_increment;
  }

  if (
    starting_ul_settlement_price != null &&
    starting_ul_settlement_price !== "" &&
    !Number.isNaN(Number(starting_ul_settlement_price))
  ) {
    params.starting_ul_settlement_price = starting_ul_settlement_price;
  }

  if (
    points_count != null &&
    points_count !== "" &&
    !Number.isNaN(Number(points_count))
  ) {
    params.points_count = points_count;
  }

  if (
    days_elapsed != null &&
    days_elapsed !== "" &&
    !Number.isNaN(Number(days_elapsed))
  ) {
    params.days_elapsed = days_elapsed;
  }

  if (
    volatility_override != null &&
    volatility_override !== "" &&
    !Number.isNaN(Number(volatility_override))
  ) {
    params.volatility_override = volatility_override;
  }

  if (
    risk_free_override != null &&
    risk_free_override !== "" &&
    !Number.isNaN(Number(risk_free_override))
  ) {
    params.risk_free_override = risk_free_override;
  }

  if (typeof popup_session === "string" && popup_session.trim() !== "") {
    params.popup_session = popup_session.trim();
  }

  return params;
}

/** Laravel expects `ids[]=` — axios 0.27 would otherwise emit `ids[0]=`. */
export function serializePayoutCompareParams(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (key === "ids" && Array.isArray(value)) {
      value.forEach((id) => {
        if (id != null && id !== "") search.append("ids[]", id);
      });
      return;
    }
    if (value == null || value === "") return;
    search.append(key, value);
  });
  return search.toString();
}

export function unwrapPayoutCompareRaw(queryResult) {
  if (!queryResult || typeof queryResult !== "object") return null;
  if (Array.isArray(queryResult.lines) || queryResult.title != null) {
    return queryResult;
  }
  const body = queryResult.data ?? queryResult;
  const raw = body?.data ?? body;
  return raw && typeof raw === "object" ? raw : null;
}

export async function fetchPayoutCompareResolved(ids, options = {}) {
  const params = buildPayoutCompareQueryParams({ ...options, ids });
  const response = await fetchPayoutCompare(params);
  return unwrapPayoutCompareRaw(response);
}

export function parsePayoutComparePremium(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Package premium for the selected Bid/Ask, copied onto the popup option. */
export function getPayoutCompareNetPremium(option, side) {
  if (!option) return null;
  const fromSide =
    side === PAYOUT_PRICING_SIDES.ASK
      ? option.ask
      : side === PAYOUT_PRICING_SIDES.BID
        ? option.bid
        : null;
  const n = parsePayoutComparePremium(fromSide ?? option.net_premium);
  if (n == null || n === 0) return null;
  return n;
}

/**
 * Copy summary bid/ask once when Payout: compare opens. Later Pricer edits must
 * not change the popup's % of Premium scale.
 */
export function snapshotPayoutComparePremiums(rows, summaryIds) {
  const wanted = new Set((summaryIds ?? []).map((id) => Number(id)));
  const out = {};
  if (!Array.isArray(rows) || !wanted.size) return out;

  rows.forEach((row) => {
    const id = Number(row?.summary_id ?? row?.summary_reference_id);
    if (!wanted.has(id)) return;
    const bid = parsePayoutComparePremium(row?.bid ?? row?.summary_agg?.bid);
    const ask = parsePayoutComparePremium(row?.ask ?? row?.summary_agg?.ask);
    const existing = out[id];
    if (!existing) {
      out[id] = { bid, ask };
      return;
    }
    if (existing.bid == null && bid != null) existing.bid = bid;
    if (existing.ask == null && ask != null) existing.ask = ask;
  });
  return out;
}

export function seedPayoutCompareLocalOptions({
  options,
  lines,
  premiumsBySummaryId = {},
} = {}) {
  const byId = new Map();

  const add = (item) => {
    const id = Number(item?.summary_id);
    if (!Number.isFinite(id)) return;
    const existing = byId.get(id) ?? {};
    const premiums =
      premiumsBySummaryId[id] ?? premiumsBySummaryId[String(id)] ?? {};
    byId.set(id, {
      summary_id: id,
      label: item.label ?? existing.label ?? item.shorthand ?? "",
      shorthand: item.shorthand ?? existing.shorthand ?? item.label ?? "",
      visible: existing.visible ?? true,
      bid: parsePayoutComparePremium(existing.bid ?? item.bid ?? premiums.bid),
      ask: parsePayoutComparePremium(existing.ask ?? item.ask ?? premiums.ask),
      net_premium: parsePayoutComparePremium(
        existing.net_premium ?? item.net_premium
      ),
    });
  };

  (options ?? []).forEach(add);
  (lines ?? []).forEach(add);
  return [...byId.values()];
}

export function getPayoutCompareYAxisTitle(compareData, yMode) {
  if (yMode === PAYOUT_COMPARE_Y_MODES.PERCENT) return "% of premium";
  const fromApi = String(compareData?.y_axis?.title ?? "")
    .replace(/\\\//g, "/")
    .trim();
  if (fromApi) return fromApi;
  const unit = String(compareData?.units?.payout ?? "").replace(/\\\//g, "/");
  return unit ? `P&L (${unit})` : "P&L";
}

/**
 * Filter to the popup's ticked options and optionally rescale Y to % of
 * each option's copied net premium. `colorIndex` keeps line colours stable
 * when a row is hidden.
 */
export function buildPayoutComparePlottedLines(
  lines,
  { localOptions, yMode, side } = {}
) {
  const optionById = new Map(
    (localOptions ?? []).map((opt) => [Number(opt.summary_id), opt])
  );
  const hideUnchecked = optionById.size > 0;

  return (lines ?? []).flatMap((line, index) => {
    if (!line?.data?.length) return [];
    const summaryId =
      line.summary_id == null ? null : Number(line.summary_id);
    const option = summaryId == null ? undefined : optionById.get(summaryId);
    if (hideUnchecked && option?.visible === false) return [];
    if (hideUnchecked && summaryId != null && !option) return [];

    let data = line.data;
    if (yMode === PAYOUT_COMPARE_Y_MODES.PERCENT) {
      const net = getPayoutCompareNetPremium(option, side);
      if (net == null) return [];
      const absPrem = Math.abs(net);
      data = line.data.map(([x, y]) => [x, (Number(y) / absPrem) * 100]);
    }

    return [{ ...line, data, colorIndex: index }];
  });
}

export function buildPayoutCompareChartSeries(lines) {
  return (lines ?? [])
    .filter((line) => line?.data?.length)
    .map((line, index) => ({
      name: line.label ?? `Option ${index + 1}`,
      data: line.data,
      lineWidth: 2,
      color:
        COMPARE_LINE_COLORS[
          (line.colorIndex ?? index) % COMPARE_LINE_COLORS.length
        ],
      expandConstantAcrossAxis: true,
    }));
}

export function buildPayoutCompareTableRows(lines) {
  const byX = new Map();
  const labels = [];

  (lines ?? []).forEach((line) => {
    const label = line?.label ?? "";
    labels.push(label);
    (line?.data ?? []).forEach(([x, y]) => {
      const xn = Number(x);
      if (Number.isNaN(xn)) return;
      if (!byX.has(xn)) byX.set(xn, {});
      byX.get(xn)[label] = y;
    });
  });

  const rows = [...byX.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([ul, values]) => ({ ul, values }));

  return { labels, rows };
}

export function formatPayoutCompareApiErrors(errors) {
  return formatPayoutAnalysisApiErrors(errors);
}

export function formatPayoutCompareClipboardText(data, shareUrl) {
  const url = typeof shareUrl === "string" ? shareUrl.trim() : "";
  const shorthands = (data?.options ?? [])
    .map((option) => String(option?.shorthand ?? option?.label ?? "").trim())
    .filter(Boolean);

  if (!shorthands.length) return url || null;
  if (!url) return shorthands.join("\n");
  return `${shorthands.join("\n")}\n${url}`;
}
